import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { columnsDataUsers } from "./variables/columnsData";
import UsersTable from "./components/UsersTable";
import Card from "components/card/Card";
import CreateUserModal from "components/modals/CreateUserModal";
import Pagination from "components/pagination";
import { useDispatch, useSelector } from 'react-redux';
import { createUser, fetchUsers } from '../../../features/users/actions';
import { useToastNotification } from 'utils/toast-utils';

const UserManagement = () => {
  const dispatch = useDispatch();
  const { showToast } = useToastNotification();
  const { users, loading } = useSelector((state) => state.users);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsShown, setIsDetailsShown] = useState(false);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const [searchTerm, setSearchTerm] = useState("");
  const bgInput = useColorModeValue("transparent", "navy.900");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchUsers({ page: 1, limit: 0 })); // Fetch all users
  }, [dispatch]);

  const filteredData = useMemo(() => {
    return users.filter(
      (user) =>
        user?.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((data, page, limit) => {
    setCurrentPage(page);
    setItemsPerPage(limit);
  }, []);

  const handleCreateUser = async (userData) => {
    try {
      await dispatch(createUser(userData)).unwrap();
      showToast('Success', 'User created successfully', 'success');
      setIsCreateModalOpen(false);
      setCurrentPage(1);
      dispatch(fetchUsers({ page: 1, limit: 0 }));
    } catch (error) {
      showToast('Error', error.message || 'Failed to create user', 'error');
    }
  };

  return (
    <>
      <Card borderRadius="0" p="0">
        {!isDetailsShown && (
          <Flex direction="column">
            <Flex justify="space-between" align="center" mb={4}>
              <Text color={textColor} fontSize="30px" fontWeight="700" lineHeight="32px">
                Users
              </Text>
              <Flex gap={4}>
                <Flex direction="column" align="center" >
                  <Input
                    placeholder="Search"
                    borderRadius="4px"
                    size="md"
                    width="218px"
                    height="40px"
                    bg={bgInput}
                    _placeholder={{ color: "gray.400" }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Flex>
                <Button
                  color='white'
                  colorScheme="purple"
                  size="md"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  Add user
                </Button>
              </Flex>
            </Flex>
          </Flex>
        )}
        <Card borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
          <UsersTable
            columnsData={columnsDataUsers}
            tableData={loading ? [] : currentPageData}
            onDetailsToggle={setIsDetailsShown}
          />
          {!isDetailsShown && (
            <Pagination
              data={filteredData}
              onPageChange={handlePageChange}
              initialItemsPerPage={itemsPerPage}
              currentPage={currentPage}
              totalItems={totalItems}
              totalPages={totalPages}
              hasMoreData={currentPage < totalPages}
            />
          )}
        </Card>
      </Card>
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
      />
    </>
  );
};

export default UserManagement;
